import React from "react";
import FieldCard from "./FieldCard";
import InputField from "./InputField";

function ItemCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 p-4">
      <div className="mb-3 text-sm font-semibold text-white">{title}</div>
      {children}
    </div>
  );
}

export default function WindowsDoorsEditor({
  windows = [],
  setWindows,
  doors = [],
  setDoors,
  errors = {},
}) {
  const addWindow = () => {
    setWindows((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: crypto.randomUUID(),
        name: "",
        widthM: "",
        heightM: "",
        frameMaterial: "wood",
        glazingType: "double",
        openingPrepRequired: false,
        interiorTrimRequired: false,
        exteriorFinishingRequired: false,
      },
    ]);
  };

  const addDoor = () => {
    setDoors((prev) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: crypto.randomUUID(),
        name: "",
        widthM: "",
        heightM: "",
        doorType: "standard",
        material: "wood",
        openingPrepRequired: false,
        interiorTrimRequired: false,
        exteriorFinishingRequired: false,
      },
    ]);
  };

  const updateWindow = (index, field, value) => {
    setWindows((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
    });
  };

  const updateDoor = (index, field, value) => {
    setDoors((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
    });
  };

  const removeWindow = (index) => {
    setWindows((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.filter((_, i) => i !== index);
    });
  };

  const removeDoor = (index) => {
    setDoors((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      return safePrev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-700 bg-gray-900 p-4 text-sm text-gray-400">
        Add each window and door separately. Use actual opening size or product
        size, depending on how you price the job.
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addWindow}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add window
        </button>
        <button
          type="button"
          onClick={addDoor}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Add door
        </button>
      </div>

      <div className="space-y-4">
        {windows.map((item, index) => (
          <ItemCard
            key={item.id || `window-${index}`}
            title={item.name?.trim() || `Window ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FieldCard
                label="Window name"
                warning="Optional internal name like Kitchen window or South facade window."
              >
                <InputField
                  value={item.name ?? ""}
                  onChange={(e) => updateWindow(index, "name", e.target.value)}
                  placeholder="e.g. Kitchen window"
                />
              </FieldCard>

              <FieldCard
                label="Width"
                unit="m"
                error={errors[`windows.${index}`]}
                warning="Visible width or opening width."
              >
                <InputField
                  type="number"
                  value={item.widthM ?? ""}
                  onChange={(e) =>
                    updateWindow(
                      index,
                      "widthM",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 1.2"
                />
              </FieldCard>

              <FieldCard
                label="Height"
                unit="m"
                error={errors[`windows.${index}`]}
                warning="Visible height or opening height."
              >
                <InputField
                  type="number"
                  value={item.heightM ?? ""}
                  onChange={(e) =>
                    updateWindow(
                      index,
                      "heightM",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 1.2"
                />
              </FieldCard>

              <FieldCard label="Frame material">
                <select
                  value={item.frameMaterial || "wood"}
                  onChange={(e) =>
                    updateWindow(index, "frameMaterial", e.target.value)
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="wood">Wood</option>
                  <option value="aluminium">Aluminium</option>
                  <option value="pvc">PVC</option>
                  <option value="composite">Composite</option>
                </select>
              </FieldCard>

              <FieldCard label="Glazing type">
                <select
                  value={item.glazingType || "double"}
                  onChange={(e) =>
                    updateWindow(index, "glazingType", e.target.value)
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="single">Single glazing</option>
                  <option value="double">Double glazing</option>
                  <option value="triple">Triple glazing</option>
                </select>
              </FieldCard>

              <FieldCard label="Opening prep required">
                <select
                  value={item.openingPrepRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateWindow(
                      index,
                      "openingPrepRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard label="Interior trim required">
                <select
                  value={item.interiorTrimRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateWindow(
                      index,
                      "interiorTrimRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard label="Exterior finishing required">
                <select
                  value={item.exteriorFinishingRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateWindow(
                      index,
                      "exteriorFinishingRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => removeWindow(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove window
              </button>
            </div>
          </ItemCard>
        ))}

        {doors.map((item, index) => (
          <ItemCard
            key={item.id || `door-${index}`}
            title={item.name?.trim() || `Door ${index + 1}`}
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <FieldCard
                label="Door name"
                warning="Optional internal name like Front door, Patio door, Utility room door."
              >
                <InputField
                  value={item.name ?? ""}
                  onChange={(e) => updateDoor(index, "name", e.target.value)}
                  placeholder="e.g. Front door"
                />
              </FieldCard>

              <FieldCard
                label="Width"
                unit="m"
                error={errors[`doors.${index}`]}
                warning="Use actual door or opening width."
              >
                <InputField
                  type="number"
                  value={item.widthM ?? ""}
                  onChange={(e) =>
                    updateDoor(
                      index,
                      "widthM",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 0.9"
                />
              </FieldCard>

              <FieldCard
                label="Height"
                unit="m"
                error={errors[`doors.${index}`]}
                warning="Use actual door or opening height."
              >
                <InputField
                  type="number"
                  value={item.heightM ?? ""}
                  onChange={(e) =>
                    updateDoor(
                      index,
                      "heightM",
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 2.1"
                />
              </FieldCard>

              <FieldCard label="Door type">
                <select
                  value={item.doorType || "standard"}
                  onChange={(e) => updateDoor(index, "doorType", e.target.value)}
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="standard">Standard door</option>
                  <option value="entrance">Entrance door</option>
                  <option value="patio">Patio / terrace door</option>
                  <option value="sliding">Sliding door</option>
                </select>
              </FieldCard>

              <FieldCard label="Material">
                <select
                  value={item.material || "wood"}
                  onChange={(e) => updateDoor(index, "material", e.target.value)}
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="wood">Wood</option>
                  <option value="aluminium">Aluminium</option>
                  <option value="steel">Steel</option>
                  <option value="composite">Composite</option>
                </select>
              </FieldCard>

              <FieldCard label="Opening prep required">
                <select
                  value={item.openingPrepRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateDoor(
                      index,
                      "openingPrepRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard label="Interior trim required">
                <select
                  value={item.interiorTrimRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateDoor(
                      index,
                      "interiorTrimRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>

              <FieldCard label="Exterior finishing required">
                <select
                  value={item.exteriorFinishingRequired ? "yes" : "no"}
                  onChange={(e) =>
                    updateDoor(
                      index,
                      "exteriorFinishingRequired",
                      e.target.value === "yes"
                    )
                  }
                  className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </FieldCard>
            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => removeDoor(index)}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Remove door
              </button>
            </div>
          </ItemCard>
        ))}

        {windows.length === 0 && doors.length === 0 && (
          <div className="rounded-xl border border-amber-700 bg-amber-950/10 p-4 text-sm text-amber-300">
            {errors.windows || "No windows or doors added yet."}
          </div>
        )}
      </div>
    </div>
  );
}